import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';
import PublicationCard from '@/features/publication/components/PublicationCard';

// Altmetric 스크립트 삭제 유틸
const removeAltmetricScript = () => {
  // 스크립트 태그 삭제
  const existingScript = document.querySelector('script[src*="d1bxh8uas1mnw7.cloudfront.net/assets/embed.js"]');
  if (existingScript && existingScript.parentNode) {
    existingScript.parentNode.removeChild(existingScript);
  }
  // window.altmetric 초기화
  if (typeof window !== 'undefined' && window.altmetric) {
    try {
      delete window.altmetric;
    } catch (e) {
      // ignore
    }
  }
  // altmetric-embed-js 태그도 삭제 (embed.js가 생성한 태그)
  const embedScript = document.getElementById('altmetric-embed-js');
  if (embedScript && embedScript.parentNode) {
    embedScript.parentNode.removeChild(embedScript);
  }
};

// Altmetric 스크립트 로드 유틸
const loadAltmetricScript = () => {
  return new Promise((resolve, reject) => {
    // 이미 로드되어 있는지 확인
    const existingScript = document.querySelector('script[src*="d1bxh8uas1mnw7.cloudfront.net/assets/embed.js"]');
    if (existingScript) {
      // 이미 로드되어 있고 window.altmetric가 준비되었는지 확인
      const checkAltmetric = (retries = 10) => {
        if (typeof window !== 'undefined' && window.altmetric && typeof window.altmetric.process === 'function') {
          resolve();
          return;
        }
        if (retries > 0) {
          setTimeout(() => checkAltmetric(retries - 1), 200);
        } else {
          resolve(); // 타임아웃되어도 resolve (재시도는 초기화 로직에서)
        }
      };
      checkAltmetric();
      return;
    }

    // 스크립트 동적 로드
    const script = document.createElement('script');
    script.src = 'https://d1bxh8uas1mnw7.cloudfront.net/assets/embed.js';
    script.async = true;
    document.body.appendChild(script);

    const script2 = document.createElement('script');
    script2.src = 'https://cdn.plu.mx/widget-popup.js';
    script2.async = true;
    document.body.appendChild(script2);
  });
};

export default function AuthorOtherPapers({ resourceId, scholarIds = [], perPage = 3 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const navigate = useNavigate();
  const hasAuthors = Array.isArray(scholarIds) && scholarIds.length > 0;

  useEffect(() => {
    if (!resourceId || !hasAuthors) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        // Build query manually to support scholar_id[]
        const sp = new URLSearchParams();
        sp.set('not_resource_id', String(resourceId));
        scholarIds.forEach((id) => sp.append('scholar_id[]', String(id)));
        sp.set('order_by', 'date');
        sp.set('order', 'desc');
        sp.set('record_per_page', String(perPage));
        sp.set('current_page', '1');
        const url = `/rm/v1/publications?${sp.toString()}`;
        const data = await get(url);
        const records = data?.result?.records || data?.result || [];
        if (!alive) return;
        setRows(Array.isArray(records) ? records : []);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [resourceId, hasAuthors, perPage, scholarIds]);

  // AuthorOtherPapers의 PublicationCard들이 렌더링된 후 altmetric 초기화
  // 주의: 스크립트는 부모 컴포넌트(Publication.jsx)에서 로드되므로, 여기서는 스크립트가 로드될 때까지 기다림
  useEffect(() => {
    // loading이 완료되고 rows가 있을 때만 실행
    if (loading || !rows || rows.length === 0) {
      return;
    }

    // 먼저 배지가 이미 있는지 확인 (HTML에 이미 렌더링되어 있는 경우)
    const container = document.querySelector('.author-other-papers-container');
    if (container) {
      const elements = container.querySelectorAll('.altmetric-embed');
      if (elements.length > 0) {
        let allHaveBadges = true;
        elements.forEach((el) => {
          const hasBadge = el.querySelector('img[src*="badges.altmetric.com"]') ||
            el.querySelector('.altmetric-badge') ||
            el.querySelector('[id^="altmetric-"]') ||
            el.querySelector('a[href*="altmetric.com"]');
          if (!hasBadge) {
            allHaveBadges = false;
          }
        });

        if (allHaveBadges) {
          // 이미 모든 배지가 HTML에 렌더링되어 있으면 초기화 불필요
          return; // useEffect 종료
        }
      }
    }

    let isMounted = true;
    let checkInterval = null;
    let timers = [];
    let initAttempted = false;

    const initAltmetric = () => {
      if (!isMounted) {
        return false;
      }
      if (typeof window === 'undefined' || !window.altmetric || typeof window.altmetric.process !== 'function') {
        return false;
      }

      // AuthorOtherPapers 영역 내의 altmetric-embed만 찾기
      const container = document.querySelector('.author-other-papers-container');
      if (!container) {
        return false;
      }

      const elements = container.querySelectorAll('.altmetric-embed');

      if (elements.length === 0) {
        return false;
      }

      // 이미 모든 요소에 배지가 있는지 확인
      let allHaveBadges = true;
      let processed = 0;
      elements.forEach((el, index) => {
        try {
          // 배지가 있는지 확인: img 태그나 altmetric-badge 클래스, 또는 id가 altmetric-로 시작하는 요소, 또는 a 태그
          const hasBadge = el.querySelector('img[src*="badges.altmetric.com"]') ||
            el.querySelector('.altmetric-badge') ||
            el.querySelector('[id^="altmetric-"]') ||
            el.querySelector('a[href*="altmetric.com"]');

          if (hasBadge) {
            return; // 이미 배지가 있으면 스킵
          }
          allHaveBadges = false; // 배지가 없는 요소가 있음

          // 기존 처리 플래그 제거 (재처리를 위해)
          delete el.dataset.altmetricProcessed;
          // const doi = el.dataset.doi;
          window.altmetric.process(el);
          el.dataset.altmetricProcessed = 'true';
          processed++;
        } catch (e) {
          console.error('[AuthorOtherPapers] Altmetric process error:', e);
        }
      });

      // 모든 요소에 배지가 있거나 처리했으면 초기화 완료
      if (allHaveBadges || processed > 0) {
        initAttempted = true;
        // 초기화 완료 시 모든 체크 중단
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
        return true;
      }

      return false;
    };

    // 초기화 시도 함수 (cleanup 함수 사용)
    const tryInit = () => {
      if (!isMounted || initAttempted) {
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
        timers.forEach(t => clearTimeout(t));
        timers = [];
        return;
      }

      // DOM 렌더링 완료를 보장하기 위해 여러 단계의 지연 사용
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!isMounted || initAttempted) {
            cleanup();
            return;
          }

          // 첫 번째 시도 (DOM 렌더링 후)
          const timer1 = setTimeout(() => {
            if (!isMounted || initAttempted) {
              cleanup();
              return;
            }
            if (initAltmetric()) {
              // 초기화 성공 시 모든 체크 중단
              cleanup();
              return;
            }

            // 첫 시도 실패시 재시도
            const timer2 = setTimeout(() => {
              if (!isMounted || initAttempted) {
                cleanup();
                return;
              }
              if (initAltmetric()) {
                // 초기화 성공 시 모든 체크 중단
                cleanup();
                return;
              }

              // 두 번째 재시도
              const timer3 = setTimeout(() => {
                if (!isMounted || initAttempted) {
                  cleanup();
                  return;
                }
                if (initAltmetric()) {
                  // 초기화 성공 시 모든 체크 중단
                  cleanup();
                  return;
                }

                // 세 번째 재시도
                const timer4 = setTimeout(() => {
                  if (!isMounted || initAttempted) {
                    cleanup();
                    return;
                  }
                  if (initAltmetric()) {
                    // 초기화 성공 시 모든 체크 중단
                    cleanup();
                  }
                }, 1000);
                timers.push(timer4);
              }, 800);
              timers.push(timer3);
            }, 500);
            timers.push(timer2);
          }, 500); // 첫 시도 지연 시간
          timers.push(timer1);
        });
      });
    };

    // 스크립트가 이미 로드되어 있는지 확인
    let checkCount = 0;
    const maxChecks = 150; // 최대 30초 (200ms * 150 = 30000ms)
    let lastScriptState = null; // 이전 스크립트 상태 추적
    let checkIntervalDelay = 200; // 초기 체크 간격 (200ms)
    let isSlowCheckMode = false; // 느린 체크 모드 (3초마다)

    // 모든 체크와 타이머 정리 함수
    const cleanup = () => {
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
      timers.forEach(t => clearTimeout(t));
      timers = [];
    };

    const checkScriptAndInit = () => {
      // 최대 체크 횟수 초과 시 포기
      if (checkCount >= maxChecks) {
        console.warn('[AuthorOtherPapers] Max check count reached, giving up');
        cleanup();
        return true; // 더 이상 체크하지 않음
      }

      checkCount++;

      // 스크립트 태그가 있는지 확인
      const existingScript = document.querySelector('script[src*="d1bxh8uas1mnw7.cloudfront.net/assets/embed.js"]');
      const hasScript = !!existingScript;
      const hasAltmetric = typeof window !== 'undefined' && window.altmetric && typeof window.altmetric.process === 'function';

      // 스크립트가 사라졌다가 다시 나타났는지 확인 (재로드 중)
      if (lastScriptState === true && !hasScript) {
        checkCount = 0; // 재로드 중이면 체크 카운트 리셋
        lastScriptState = false;
        return false; // 재로드 완료를 기다림
      }
      lastScriptState = hasScript;

      // 이미 초기화가 완료되었으면 체크 중단
      if (initAttempted) {
        cleanup();
        return true; // 더 이상 체크하지 않음
      }

      // 배지가 이미 있는지 먼저 확인 (window.altmetric가 없어도 배지가 있으면 초기화 완료)
      const container = document.querySelector('.author-other-papers-container');
      if (container) {
        const elements = container.querySelectorAll('.altmetric-embed');
        if (elements.length > 0) {
          let allHaveBadges = true;
          let hasAnyBadge = false;
          elements.forEach((el) => {
            // 배지가 있는지 확인: img 태그나 altmetric-badge 클래스, 또는 id가 altmetric-로 시작하는 요소
            const hasBadge = el.querySelector('img[src*="badges.altmetric.com"]') ||
              el.querySelector('.altmetric-badge') ||
              el.querySelector('[id^="altmetric-"]') ||
              el.querySelector('a[href*="altmetric.com"]');
            if (hasBadge) {
              hasAnyBadge = true;
            } else {
              allHaveBadges = false;
            }
          });

          if (allHaveBadges) {
            // 이미 모든 배지가 있으면 초기화 완료 (window.altmetric가 없어도)
            initAttempted = true;
            cleanup();
            return true; // 더 이상 체크하지 않음
          }

          // 일부 배지가 있으면 HTML이 정상적으로 렌더링된 것으로 간주
          // window.altmetric가 없어도 배지가 HTML에 있으면 추가 초기화 불필요
          if (hasAnyBadge && !hasAltmetric) {
            // window.altmetric가 준비될 때까지 대기 (하지만 계속 체크하지 않음)
            // 최대 체크 횟수를 줄여서 빠르게 포기
            if (checkCount >= 50) { // 10초 후 포기
              initAttempted = true;
              cleanup();
              return true;
            }
          }
        }
      }

      // 배지가 HTML에 없고 window.altmetric가 없거나 작동하지 않을 때
      // 일정 시간(10초) 후에도 배지가 나타나지 않으면 느린 체크 모드로 전환 (3초마다)
      if (checkCount === 50 && !isSlowCheckMode) { // 10초 후 (50 * 200ms)
        const container = document.querySelector('.author-other-papers-container');
        if (container) {
          const elements = container.querySelectorAll('.altmetric-embed');
          let hasAnyBadgeInHTML = false;
          elements.forEach((el) => {
            const hasBadge = el.querySelector('img[src*="badges.altmetric.com"]') ||
              el.querySelector('.altmetric-badge') ||
              el.querySelector('[id^="altmetric-"]') ||
              el.querySelector('a[href*="altmetric.com"]');
            if (hasBadge) {
              hasAnyBadgeInHTML = true;
            }
          });

          // 배지가 HTML에 없고 window.altmetric가 없으면 느린 체크 모드로 전환
          if (!hasAnyBadgeInHTML && !hasAltmetric) {
            isSlowCheckMode = true;
            checkIntervalDelay = 3000; // 3초로 변경
            // 기존 interval 정리하고 새로운 간격으로 재시작
            if (checkInterval) {
              clearInterval(checkInterval);
              checkInterval = null;
            }
            // 3초마다 체크하는 새로운 interval 시작
            checkInterval = setInterval(() => {
              if (!isMounted || initAttempted) {
                cleanup();
                return;
              }
              checkScriptAndInit();
            }, checkIntervalDelay);
            return false;
          }
        }
      }

      if (hasAltmetric) {
        // window.altmetric가 준비되었으면 초기화
        cleanup();
        tryInit();
        return true;
      }

      return false;
    };

    // 즉시 체크
    if (!checkScriptAndInit()) {
      // 스크립트가 아직 로드되지 않았으면 대기 후 재시도 (부모 컴포넌트에서 로드 중)
      checkInterval = setInterval(() => {
        if (!isMounted || initAttempted) {
          cleanup();
          return;
        }
        if (checkScriptAndInit()) {
          // checkScriptAndInit이 true를 반환하면 더 이상 체크하지 않음
          cleanup();
        }
      }, checkIntervalDelay);

      // 최대 30초 대기 (부모 컴포넌트의 스크립트 로드 시간 고려)
      const maxWaitTimer = setTimeout(() => {
        cleanup();
        // 타임아웃 후에도 한 번 더 시도
        if (!initAttempted && isMounted) {
          checkScriptAndInit();
          cleanup();
        }
      }, 30000);
      timers.push(maxWaitTimer);
    }

    return () => {
      isMounted = false;
      cleanup();
    };
  }, [rows, loading]);

  const moreHref = useMemo(() => {
    const sp = new URLSearchParams();
    // 81-86 라인과 동일한 파라미터 구조 사용
    if (resourceId) {
      sp.set('not_resource_id', String(resourceId));
    }
    if (hasAuthors) {
      scholarIds.forEach((id) => sp.append('scholar_id[]', String(id)));
    }
    sp.set('order_by', 'date');
    sp.set('order', 'desc');
    sp.set('record_per_page', String(perPage));
    sp.set('current_page', '1');
    const queryString = sp.toString();
    return queryString ? `/publications?${queryString}` : '/publications';
  }, [resourceId, hasAuthors, scholarIds, perPage]);

  return (
    <div className="author-other-papers-container hidden mt-[60px] lg:block lg:mt-[80px]">
      <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
        Other papers published by the author
      </h2>

      <ul className="flex flex-col gap-4">
        {loading && rows.length === 0 && (
          <li className="p-8 bg-white border border-[#E4E8F1] rounded-xl relative">
            <ChartSkeletonLoader show={loading} />
          </li>
        )}
        {err && (
          <li className="p-8 bg-white border border-[#E4E8F1] rounded-xl">
            <div className="text-[14px] text-red-600">{String(err)}</div>
          </li>
        )}
        {!loading && !err && rows.length === 0 && (
          <li className="p-8 bg-white border border-[#E4E8F1] rounded-xl">
            <div className="text-[14px] text-[#8D9AAF]">No other papers found.</div>
          </li>
        )}
        {rows.map((item) => (
          <PublicationCard key={item.resource_id || item.id} item={item} />
        ))}
      </ul>

      <div className="text-center mt-10">
        <a
          href={moreHref}
          onClick={(e) => {
            try { if (e && typeof e.preventDefault === 'function') e.preventDefault(); } catch (err) { }
            try { navigate(moreHref); } catch (err) { window.location.href = moreHref; }
          }}
          className="inline-flex items-center justify-center gap-1 py-3 px-8 bg-[#F3F5F9] border border-[#E4E8F1] rounded-xl"
        >
          <span className="text-[14px] text-[#6C7687]">More</span>
          <img src={`${rmVars.themeUrl}images/launch-ico-6C7687.png`} className="w-[16px]" alt="link" />
        </a>
      </div>
    </div>
  );
}
